import React, { useState, useRef } from 'react';
import { importApi } from '../api';

const SCHRITTE = ['Datei wählen', 'Mapping', 'Testlauf', 'Importieren', 'Ergebnis'];
const TYP_OPTIONEN = [
  { value: 'vertraege', label: 'Verträge' },
  { value: 'kontakte', label: 'Kontakte' },
  { value: 'anbieter', label: 'Anbieter' },
  { value: 'kosten', label: 'Kostenpositionen' },
];

export default function ImportPage() {
  const [schritt, setSchritt] = useState(0);
  const [typ, setTyp] = useState('vertraege');
  const [format, setFormat] = useState('csv');
  const [datei, setDatei] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [vorschau, setVorschau] = useState(null);
  const [mapping, setMapping] = useState({});
  const [testErgebnis, setTestErgebnis] = useState(null);
  const [importErgebnis, setImportErgebnis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const fileRef = useRef();

  const handleDatei = async () => {
    if (!datei) { setErr('Bitte eine Datei auswählen.'); return; }
    setLoading(true); setErr(null);
    try {
      const r = await importApi.upload(datei, typ, format);
      setJobId(r.job_id);
      setVorschau(r);
      const autoMapping = {};
      (r.spalten || []).forEach(s => { autoMapping[s] = s; });
      setMapping(autoMapping);
      setSchritt(1);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const handleTestlauf = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await importApi.testlauf(jobId, mapping);
      setTestErgebnis(r);
      setSchritt(2);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!window.confirm(`${testErgebnis?.gueltig || 0} Zeilen importieren?`)) return;
    setLoading(true); setErr(null);
    try {
      const r = await importApi.execute(jobId, mapping);
      setImportErgebnis(r);
      setSchritt(4);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const handleRollback = async () => {
    if (!window.confirm('Import rückgängig machen?')) return;
    setLoading(true);
    try {
      await importApi.rollback(jobId);
      setImportErgebnis(prev => ({ ...prev, rolled_back: true }));
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const reset = () => {
    setSchritt(0); setDatei(null); setJobId(null); setVorschau(null);
    setMapping({}); setTestErgebnis(null); setImportErgebnis(null); setErr(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div style={{ padding: 20, color: '#f1f5f9', maxWidth: 900 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 20px' }}>📥 Import</h1>

      {/* Fortschrittsanzeige */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, overflowX: 'auto' }}>
        {SCHRITTE.map((s, i) => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
              background: i < schritt ? '#22c55e' : i === schritt ? '#6366f1' : '#334155',
              color: '#fff', flexShrink: 0
            }}>{i < schritt ? '✓' : i + 1}</div>
            <span style={{ fontSize: 12, color: i === schritt ? '#f1f5f9' : '#64748b', whiteSpace: 'nowrap' }}>{s}</span>
            {i < SCHRITTE.length - 1 && <div style={{ width: 20, height: 1, background: '#334155', flexShrink: 0 }} />}
          </div>
        ))}
      </div>

      {err && <div style={{ background: '#7f1d1d', padding: 12, borderRadius: 8, marginBottom: 14 }}>{err}</div>}

      {/* Schritt 0: Datei wählen */}
      {schritt === 0 && (
        <div style={card}>
          <h2 style={h2}>Schritt 1: Importtyp und Datei</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={lbl}>Importtyp</label>
              <select value={typ} onChange={e => setTyp(e.target.value)} style={sel}>
                {TYP_OPTIONEN.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Format</label>
              <select value={format} onChange={e => setFormat(e.target.value)} style={sel}>
                <option value="csv">CSV</option>
                <option value="json">JSON</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={lbl}>Datei auswählen (max. 10 MB, max. 5.000 Zeilen)</label>
            <input ref={fileRef} type="file" accept=".csv,.json" onChange={e => setDatei(e.target.files[0])}
              style={{ ...sel, padding: '6px 10px' }} />
          </div>
          <div style={{ background: '#0f172a', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: '#64748b' }}>
            <strong style={{ color: '#94a3b8' }}>Sicherheitshinweise:</strong>
            <ul style={{ margin: '6px 0 0 16px', lineHeight: 1.8 }}>
              <li>Importdaten werden zunächst als Testlauf geprüft – keine Schreiboperationen.</li>
              <li>Erst nach expliziter Bestätigung werden Daten in die Produktivtabellen geschrieben.</li>
              <li>Ein Rollback ist nach dem Import möglich.</li>
              <li>Formelinjektionen (= + − @) werden automatisch neutralisiert.</li>
            </ul>
          </div>
          <button onClick={handleDatei} disabled={loading || !datei} className="btn-primary">
            {loading ? '⏳ Lade…' : 'Weiter →'}
          </button>
        </div>
      )}

      {/* Schritt 1: Mapping */}
      {schritt === 1 && vorschau && (
        <div style={card}>
          <h2 style={h2}>Schritt 2: Spalten-Mapping</h2>
          <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
            {vorschau.zeilen} Zeilen gefunden · {vorschau.spalten?.length} Spalten
          </p>
          <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
            {(vorschau.zielfelder || vorschau.spalten || []).map(z => (
              <div key={z} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 160, fontSize: 13, color: '#94a3b8', flexShrink: 0 }}>{z}</span>
                <span style={{ color: '#475569', fontSize: 12, flexShrink: 0 }}>→</span>
                <select value={mapping[z] || ''} onChange={e => setMapping(p => ({ ...p, [z]: e.target.value }))} style={{ ...sel, flex: 1 }}>
                  <option value="">— nicht importieren —</option>
                  {(vorschau.spalten || []).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleTestlauf} disabled={loading} className="btn-primary">
              {loading ? '⏳ Prüfe…' : 'Testlauf starten'}
            </button>
            <button onClick={reset} className="btn-secondary">Abbrechen</button>
          </div>
        </div>
      )}

      {/* Schritt 2: Testlauf */}
      {schritt === 2 && testErgebnis && (
        <div style={card}>
          <h2 style={h2}>Schritt 3: Testlauf-Ergebnis</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12, marginBottom: 20 }}>
            <KpiCard label="Gesamt" value={testErgebnis.gesamt} color="#94a3b8" />
            <KpiCard label="Gültig" value={testErgebnis.gueltig} color="#22c55e" />
            <KpiCard label="Fehler" value={testErgebnis.fehler} color="#ef4444" />
            <KpiCard label="Warnungen" value={testErgebnis.warnungen} color="#f59e0b" />
          </div>
          {(testErgebnis.fehler_details || []).length > 0 && (
            <div style={{ background: '#1a0505', border: '1px solid #ef4444', borderRadius: 8, padding: 12, marginBottom: 16 }}>
              <strong style={{ color: '#ef4444', fontSize: 13 }}>Fehler ({testErgebnis.fehler_details.length})</strong>
              <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto' }}>
                {testErgebnis.fehler_details.slice(0, 20).map((f, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#fca5a5', padding: '2px 0' }}>
                    Zeile {f.zeile}: {f.fehler}
                  </div>
                ))}
              </div>
            </div>
          )}
          <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
            ⚠ Beim Testlauf wurden keine Daten geschrieben. Der Import schreibt {testErgebnis.gueltig} Zeilen.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setSchritt(3)} disabled={testErgebnis.gueltig === 0} className="btn-primary">
              Import starten →
            </button>
            <button onClick={reset} className="btn-secondary">Abbrechen</button>
          </div>
        </div>
      )}

      {/* Schritt 3: Bestätigung */}
      {schritt === 3 && (
        <div style={card}>
          <h2 style={h2}>Schritt 4: Import bestätigen</h2>
          <div style={{ background: '#0f172a', border: '1px solid #f59e0b', borderRadius: 8, padding: 16, marginBottom: 20 }}>
            <p style={{ color: '#fbbf24', fontSize: 14, margin: 0, fontWeight: 500 }}>
              ⚠ Sie sind dabei, {testErgebnis?.gueltig} Zeilen in die Tabelle <strong>{typ}</strong> zu importieren.
            </p>
            <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 8 }}>
              Diese Aktion schreibt Daten in die Produktivdatenbank. Ein Rollback ist anschließend möglich.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleImport} disabled={loading} className="btn-primary" style={{ background: '#15803d' }}>
              {loading ? '⏳ Importiere…' : `✓ ${testErgebnis?.gueltig} Zeilen importieren`}
            </button>
            <button onClick={() => setSchritt(2)} className="btn-secondary">← Zurück</button>
          </div>
        </div>
      )}

      {/* Schritt 4: Ergebnis */}
      {schritt === 4 && importErgebnis && (
        <div style={card}>
          <h2 style={h2}>Schritt 5: Ergebnis</h2>
          {importErgebnis.rolled_back ? (
            <div style={{ background: '#1a0505', border: '1px solid #ef4444', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <strong style={{ color: '#ef4444' }}>Rollback durchgeführt.</strong> Die importierten Datensätze wurden entfernt.
            </div>
          ) : (
            <div style={{ background: '#052e16', border: '1px solid #22c55e', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <strong style={{ color: '#22c55e' }}>Import abgeschlossen.</strong> {importErgebnis.importiert} Datensätze importiert.
            </div>
          )}
          {!importErgebnis.rolled_back && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button onClick={handleRollback} disabled={loading} className="btn-secondary" style={{ borderColor: '#ef4444', color: '#fca5a5' }}>
                {loading ? '⏳…' : '⏪ Import rückgängig machen'}
              </button>
            </div>
          )}
          <button onClick={reset} className="btn-primary">Neuer Import</button>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, color }) {
  return (
    <div style={{ background: '#0f172a', border: `1px solid ${color}44`, borderRadius: 8, padding: 12, textAlign: 'center' }}>
      <div style={{ color, fontSize: 24, fontWeight: 700 }}>{value ?? '—'}</div>
      <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>{label}</div>
    </div>
  );
}

const card = { background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 20 };
const h2 = { margin: '0 0 16px', fontSize: 16, fontWeight: 600 };
const lbl = { display: 'block', color: '#94a3b8', fontSize: 12, marginBottom: 6 };
const sel = { background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', padding: '8px 10px', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' };
